import React, { useState } from 'react';
import { Flex } from 'rebass';
import {
  FormComboGroup,
  Input,
  SearchInput,
  Select,
} from '../../components/Form';
import PropTypes from 'prop-types';
import { isBrowser } from 'react-device-detect';
import useMode from '../../hooks/useMode';
import { FilterBarProps } from '../../components/FilterBar/FilterBar';
import ResetFiltersButton from '../../components/FilterBar/ResetFiltersButton';
import {
  AssemblyCompliancePicker,
  CompliantReason,
} from '../../components/BackflowAssembly/AssemblyCompliancePicker';
import {
  AssemblyTestStatusPicker,
  TestStatus,
} from '../../components/BackflowAssembly/AssemblyTestStatusPicker';
import {
  AssemblyResult,
  AssemblyResultPicker,
} from '../../components/BackflowAssembly/AssemblyResultPicker';
import {
  PropertyType,
  PropertyTypePicker,
} from '../../components/WaterService/PropertyTypePicker';
import { GenericStatusPicker } from '../../components/GenericStatusPicker';
import { AssemblyProtectionTypePicker } from '../../components/BackflowAssembly/AssemblyProtectionPicker';
import { MonthDayPicker } from '../../components/Date/MonthDayPicker';
import { MonthOfYearRangePicker } from '../../components/Date/MonthOfYearRangePicker';
import { stringSearchFields, stringSearchFieldsCompany } from './Assemblies';
import { getStringSearchPlaceholder } from '../../helpers/search';
import TagPicker from '../../components/TagPicker/TagPicker';

export const dateOptions = [
  {
    field: '',
    label: '- date -',
    defaultOperator: undefined,
  },
  {
    field: 'testAnniversary',
    label: 'Test Anniv.',
    defaultOperator: undefined,
  },
  {
    field: 'testAnniversaryMonth',
    label: 'Test Anniv. Month',
    defaultOperator: undefined,
  },
  {
    field: 'lastTestDate',
    label: 'Last Tested',
    defaultOperator: undefined,
  },
  {
    field: 'nextTestDate',
    label: 'Next Test Due',
    defaultOperator: undefined,
  },
  {
    field: 'daysUntilDue',
    label: 'Days Until Due',
    defaultOperator: 'lte',
  },
  {
    field: 'daysPastDue',
    label: 'Days Past Due',
    defaultOperator: 'gte',
  },
  // 'Coming Due',
  // 'Overdue',
] as const;

export type AssemblyFilters = {
  status?: 'ACTIVE' | 'INACTIVE';
  testStatus?: TestStatus[];
  propertyType?: PropertyType[];
  protectionType?: 'SERVICE' | 'INTERNAL';
  compliantReason?: CompliantReason[];
  lastResult?: AssemblyResult[];
  stringSearch?: string;
  tags?: string[];
  dateField?: (typeof dateOptions)[number]['field'];
  daysOperator?: 'eq' | 'gte' | 'lte';
  days?: string;
  dateStart?: string;
  dateEnd?: string;
};

export const defaultFilters: AssemblyFilters = {
  status: 'ACTIVE',
  testStatus: undefined,
  propertyType: undefined,
  lastResult: undefined,
  stringSearch: '',
  compliantReason: undefined,
  tags: undefined,
  dateField: '',
  dateStart: '',
  dateEnd: '',
};

export const getDaysOperator = (
  filters: AssemblyFilters
): string | undefined => {
  return (
    filters.daysOperator ||
    dateOptions.find(
      (value) => value.field === filters.dateField && value.defaultOperator
    )?.defaultOperator
  );
};

type FilterBarAssemblyProps = FilterBarProps<AssemblyFilters>;

/**
 * @component for jobs page
 */
const FilterBarAssembly: React.FC<FilterBarAssemblyProps> = (props) => {
  const { filters, setFilters, selectedId } = props;
  const mode = useMode();

  const onChange = (e: any) => {
    setFilters((prevState) => {
      const overrides =
        e.target.name === 'dateField' &&
        (prevState?.dateField === 'testAnniversaryMonth' ||
          e.target.value == 'testAnniversaryMonth') &&
        e.target.value !== prevState?.dateField
          ? {
              dateStart: undefined,
              dateEnd: undefined,
            }
          : {};
      return {
        ...prevState,
        ...overrides,
        [e.target.name]: e.target.value,
      };
    });
  };

  const [dateAutoFocus] = useState(!filters.dateField);

  const searchFields =
    mode === 'company' ? stringSearchFieldsCompany : stringSearchFields;

  return (
    <Flex variant="filterBar.container">
      <Flex variant="filterBar.left">
        <SearchInput
          placeholder={getStringSearchPlaceholder(searchFields)}
          autoFocus={isBrowser && !selectedId}
          width={515}
          value={filters.stringSearch}
          name="stringSearch"
          onChange={onChange}
        />
        <GenericStatusPicker
          value={filters.status}
          onChange={(value) => {
            setFilters({
              ...filters,
              status: value,
            });
          }}
        />
        <AssemblyTestStatusPicker
          value={filters.testStatus}
          onChange={(values) =>
            setFilters({
              ...filters,
              testStatus: values,
            })
          }
        />
        <AssemblyCompliancePicker
          value={filters.compliantReason}
          onChange={(values) =>
            setFilters({
              ...filters,
              compliantReason: values,
            })
          }
        />
        <AssemblyResultPicker
          value={filters.lastResult}
          onChange={(value) => {
            setFilters({
              ...filters,
              lastResult: value,
            });
          }}
        />
        <PropertyTypePicker
          isMulti={true}
          onChange={(value) => {
            setFilters({
              ...filters,
              propertyType: value,
            });
          }}
          value={filters.propertyType}
        />
        <AssemblyProtectionTypePicker
          value={filters.protectionType}
          isClearable
          onChange={(value) => {
            setFilters({
              ...filters,
              protectionType: value,
            });
          }}
        />
        {mode === 'water-supplier' && (
          <TagPicker
            value={filters.tags}
            placeholder="Tags"
            isCreatable={false}
            usedOn="ASSEMBLY"
            onChange={(newTags) =>
              setFilters((prev) => ({
                ...prev,
                tags: newTags.length > 0 ? newTags.slice() : undefined,
              }))
            }
          />
        )}
        <FormComboGroup>
          <Select
            name="dateField"
            value={filters.dateField}
            onChange={onChange}
            variant={filters.dateField ? 'selectCombo' : 'select'}
          >
            {dateOptions.map((x, i) => (
              <option value={x.field} key={i}>
                {x.label}
              </option>
            ))}
          </Select>
          {filters.dateField && (
            <>
              {['daysUntilDue', 'daysPastDue'].includes(filters.dateField) && (
                <>
                  <Select
                    value={getDaysOperator(filters)}
                    name="daysOperator"
                    onChange={onChange}
                    minWidth={70}
                    variant="inputSquare"
                  >
                    <option value="lte">{'<='}</option>
                    <option value="gte">{'>='}</option>
                    <option value="eq">{'='}</option>
                  </Select>
                  <Input
                    id="days"
                    name="days"
                    variant="inputRoundedRight"
                    type="number"
                    step="1"
                    value={filters.days}
                    minWidth={80}
                    width={80}
                    autoFocus
                    onChange={onChange}
                    autoComplete="off"
                  />
                </>
              )}
              {![
                'daysUntilDue',
                'daysPastDue',
                'testAnniversaryMonth',
              ].includes(filters.dateField) && (
                <MonthDayPicker
                  startDate={filters.dateStart}
                  endDate={filters.dateEnd}
                  autoFocus={dateAutoFocus}
                  variant="inputRoundedRight"
                  onChange={(dateStart, dateEnd) => {
                    setFilters((prevState) => ({
                      ...prevState,
                      dateStart: dateStart ?? undefined,
                      dateEnd: dateEnd ?? undefined,
                    }));
                  }}
                />
              )}
              {filters.dateField === 'testAnniversaryMonth' && (
                <MonthOfYearRangePicker
                  startDate={filters.dateStart}
                  endDate={filters.dateEnd}
                  autoFocus={dateAutoFocus}
                  variant="inputRoundedRight"
                  onChange={(dateStart, dateEnd) => {
                    setFilters((prevState) => ({
                      ...prevState,
                      dateStart: dateStart ?? undefined,
                      dateEnd: dateEnd ?? undefined,
                    }));
                  }}
                />
              )}
            </>
          )}
        </FormComboGroup>
      </Flex>

      <Flex variant="filterBar.right">
        <ResetFiltersButton setFilters={setFilters} />
      </Flex>
    </Flex>
  );
};

FilterBarAssembly.propTypes = {
  filters: PropTypes.any.isRequired,
  setFilters: PropTypes.func.isRequired,
};

export default FilterBarAssembly;
